import React, { useCallback, useEffect, useState } from "react";
import { RefreshCw, Edit3, AlertCircle, PoundSterling, Upload } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, ComposedChart, Line, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, ReferenceLine,
} from "recharts";
import { api, formatApiError } from "../../lib/api";
import MonitoringTab from "../projectDetail/Monitoring";
import WeatherCoverageHeatmap from "../../components/WeatherCoverageHeatmap";
import MeterSyncCoverageHeatmap from "../../components/MeterSyncCoverageHeatmap";
import MeterDataImportDialog from "../../components/MeterDataImportDialog";
import { KpiStrip, GenerationChart, VariationChart, RevenuePanel } from "./PortfolioOrkestra";

/** Per-site detailed dashboard.
 *
 * Shows the analytics-native summary (KPI strip, actual-vs-estimate chart with
 * variance overlay, MPAN extras when configured, and site P&L), then embeds
 * the full Projects → Monitoring tab beneath it so operators get grouping
 * toggles, series toggles, forecast overlay, CSV/PDF export, multi-site
 * compare, monthly summary, PPA contracts and weather backfill without
 * leaving the Analytics view.
 */
export default function SiteDetail({ projectId, range, rangeQuery, onEdit }) {
  const [data, setData] = useState(null);
  const [pnl, setPnl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  // Extra state for the embedded MonitoringTab — mirrors the props that
  // ProjectDetail passes when mounting it under Projects → [id] → Monitoring.
  const [project, setProject] = useState(null);
  const [design, setDesign] = useState(null);
  const [monitoring, setMonitoring] = useState(null);
  const [showMeterImport, setShowMeterImport] = useState(false);

  const rq = rangeQuery || `range=${range}`;
  const pnlRange = (() => {
    const m = rq.match(/start_date=([\d-]+)&end_date=([\d-]+)/);
    return m ? encodeURIComponent(`window:${m[1]}T00:00|${m[2]}T23:59`) : range;
  })();
  const load = useCallback(async () => {
    try {
      const [{ data: prod }, { data: pl }, projectRes, designRes, monRes] = await Promise.all([
        api.get(`/analytics/sites/${projectId}/production?${rq}`),
        api.get(`/analytics/sites/${projectId}/pnl?range=${pnlRange}`),
        api.get(`/projects/${projectId}`).catch(() => ({ data: null })),
        api.get(`/designs/by-project/${projectId}`).catch(() => ({ data: null })),
        api.get(`/monitoring/by-project/${projectId}`).catch(() => ({ data: null })),
      ]);
      setData(prod);
      setPnl(pl);
      setProject(projectRes.data);
      setDesign(designRes.data);
      setMonitoring(monRes.data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [projectId, rq, pnlRange]);

  useEffect(() => { load(); }, [load]);

  const syncNow = async () => {
    setBusy(true);
    try {
      const { data: r } = await api.post(`/analytics/sites/${projectId}/sync-15min?hours=24`);
      if (r.status === "ok") toast.success(`Wrote ${r.written} 15-min samples`);
      else if (r.status === "skipped") toast.error(`Skipped: ${r.reason}`);
      else toast.error(`Error: ${r.error}`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const backfill = async () => {
    if (!window.confirm("Pull the last 30 days of 15-min data from SolarEdge? This can take ~30s.")) return;
    setBackfilling(true);
    try {
      const { data: r } = await api.post(`/analytics/sites/${projectId}/backfill?days=30`);
      toast.success(`Backfilled ${r.written} samples (${r.errors.length} chunk errors)`);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBackfilling(false); }
  };

  if (!data) return <div className="muted font-mono text-xs uppercase tracking-widest">Loading…</div>;

  const s = data.summary;
  const isMO = (data.data_source || "solaredge") === "meteronline";
  const misconfig = isMO
    ? !data.meteronline_configured || !data.weather_configured
    : !data.solaredge_configured || !data.weather_configured;

  return (
    <div data-testid="site-detail">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div className="min-w-0">
          <div className="ork-eyebrow">Site performance</div>
          <h2 className="ork-h1 !text-3xl" data-testid="site-detail-name">{data.project_name}</h2>
          {misconfig && (
            <div className="mt-2 text-xs px-2 py-1 inline-flex items-center gap-1.5 rounded-sm"
                 style={{ background: "var(--warm-soft)", color: "var(--warm-hover)" }}
                 data-testid="site-misconfig-badge">
              <AlertCircle size={12} />
              {isMO
                ? (!data.meteronline_configured ? "MeterOnline credentials incomplete (username / API password / meter numbers)" : null)
                : (!data.solaredge_configured ? "SolarEdge credentials missing" : null)}
              {((isMO ? !data.meteronline_configured : !data.solaredge_configured) && !data.weather_configured) && " · "}
              {!data.weather_configured && "Lat/lng missing (no weather estimate)"}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={onEdit} className="btn-ghost" data-testid="site-edit-btn">
            <Edit3 size={13} /> Edit
          </button>
          <button onClick={() => setShowMeterImport(true)} className="btn-ghost" data-testid="site-meter-import-btn"
                  title="Upload a MeterOnline HH-profile export (generation / import / export)">
            <Upload size={13} /> Upload meter data
          </button>
          <button onClick={backfill} disabled={backfilling} className="btn-ghost" data-testid="site-backfill-btn">
            <RefreshCw size={13} className={backfilling ? "animate-spin" : ""} /> Backfill 30d
          </button>
          <button onClick={syncNow} disabled={busy} className="btn-accent" data-testid="site-sync-btn">
            <RefreshCw size={13} className={busy ? "animate-spin" : ""} /> Sync 24h
          </button>
        </div>
      </div>

      <KpiStrip s={s} prefix="sk" actualSub={`Metered energy produced at this site · ${s.sample_count.toLocaleString()} samples.`} fourth={
        <div data-testid="sk-range">
          <div className="ork-kpi-label">Data source</div>
          <div className="ork-kpi-value capitalize">{data.data_source || "—"}</div>
          <div className="ork-kpi-sub">{data.series.length} data points · {data.range.toUpperCase()}</div>
        </div>
      } />
      <RevenuePanel rangeQuery={rq} projectId={projectId} />

      {/* MeterOnline / HH extras — only render when the site has MPAN data */}
      {(s.import_kwh != null || s.export_kwh != null) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-8" data-testid="site-mpan-kpis">
          <div className="p-4 border border-[var(--line)]" data-testid="mk-import">
            <div className="label">Grid import</div>
            <div className="font-display text-xl font-bold mt-1">{s.import_kwh?.toLocaleString() ?? "—"} <span className="text-xs muted">kWh</span></div>
            <div className="text-xs muted">from Import MPAN</div>
          </div>
          <div className="p-4 border border-[var(--line)]" data-testid="mk-export">
            <div className="label">Grid export</div>
            <div className="font-display text-xl font-bold mt-1">{s.export_kwh?.toLocaleString() ?? "—"} <span className="text-xs muted">kWh</span></div>
            <div className="text-xs muted">from Export MPAN</div>
          </div>
          <div className="p-4 border border-[var(--line)] bg-[var(--accent)] text-white" data-testid="mk-selfcons">
            <div className="label" style={{ color: "rgba(255,255,255,0.78)" }}>Self-consumption</div>
            <div className="font-display text-xl font-bold mt-1">
              {s.self_consumption_kwh != null ? `${s.self_consumption_kwh.toLocaleString()} kWh` : "—"}
            </div>
            <div className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
              {s.self_consumption_ratio_pct != null ? `${s.self_consumption_ratio_pct.toFixed(1)}% of generation` : "add Gen MPAN"}
            </div>
          </div>
          <div className="p-4 border border-[var(--line)]" data-testid="mk-selfsuff">
            <div className="label">Self-sufficiency</div>
            <div className="font-display text-xl font-bold mt-1">
              {s.self_sufficiency_pct != null ? `${s.self_sufficiency_pct.toFixed(1)}%` : "—"}
            </div>
            <div className="text-xs muted">self-cons / total load</div>
          </div>
        </div>
      )}

      {data.series.length > 0 ? (
        <>
          <GenerationChart series={data.series} testid="site-chart" />
          <VariationChart series={data.series} testid="site-variation-chart" />
        </>
      ) : (
        <section className="ork-panel mb-8 text-sm text-center" style={{ color: "var(--ink-3)" }} data-testid="site-chart">
          No samples yet. {isMO
            ? "Configure MeterOnline username + API password + Import/Export meter numbers, then hit Sync 24h."
            : "Configure SolarEdge + lat/lng, then hit Sync 24h."}
        </section>
      )}

      {/* Weather-coverage calendar heatmap */}
      <div className="mb-8">
        <WeatherCoverageHeatmap projectId={projectId} defaultLookback={365} />
      </div>

      {/* Meter-sync coverage calendar heatmap — surfaces MeterOnline / HH
          sync gaps and lets the operator backfill them in one click. */}
      <div className="mb-8">
        <MeterSyncCoverageHeatmap projectId={projectId} defaultLookback={365}
                                  onBackfilled={load} />
      </div>

      {pnl && (
        <section className="mb-8" data-testid="site-pnl">
          <div className="flex items-end justify-between mb-4">
            <div>
              <div className="label flex items-center gap-2"><PoundSterling size={11} /> Site P&amp;L</div>
              <div className="font-display text-lg font-semibold mt-1">Revenue &minus; OpEx over {pnl.range}</div>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-[10px] muted font-mono uppercase tracking-widest" title={
                `PPA rate from ${pnl.rates.ppa_source || "default"} · ` +
                `Export from ${pnl.rates.export_source || "default"} · ` +
                `Admin from ${pnl.rates.admin_source || "default"} · ` +
                `OpEx from ${pnl.rates.opex_source}`
              }>
                PPA {pnl.rates.ppa_p_per_kwh}p ({(pnl.rates.ppa_source || "default").replace("_", " ")}) ·
                Export {pnl.rates.grid_p_per_kwh}p ({(pnl.rates.export_source || "default").replace("_", " ")}) ·
                Admin {pnl.rates.admin_p_per_kwh}p ({(pnl.rates.admin_source || "default").replace("_", " ")}) ·
                OpEx £{pnl.rates.opex_annual_gbp}/yr ({pnl.rates.opex_source.replace("_", " ")})
              </div>
              {onEdit && (
                <button onClick={onEdit} className="btn-ghost text-[10px]" data-testid="pnl-edit-rates-btn"
                        title="Edit rates & OpEx on this site">
                  <Edit3 size={10} /> Rates
                </button>
              )}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6" data-testid="pnl-kpis">
            <div className="p-4 border border-[var(--line)]" data-testid="pk-export-rev">
              <div className="label">Export revenue</div>
              <div className="font-display text-xl font-bold mt-1">£{pnl.revenue.export_gbp.toLocaleString()}</div>
              <div className="text-xs muted">{pnl.energy.export_kwh.toLocaleString()} kWh × {pnl.rates.grid_p_per_kwh}p</div>
            </div>
            <div className="p-4 border border-[var(--line)]" data-testid="pk-ppa-rev">
              <div className="label">PPA revenue</div>
              <div className="font-display text-xl font-bold mt-1">£{pnl.revenue.ppa_gbp.toLocaleString()}</div>
              <div className="text-xs muted">{pnl.energy.self_cons_kwh.toLocaleString()} kWh × {pnl.rates.ppa_p_per_kwh}p</div>
            </div>
            <div className="p-4 border border-[var(--line)]" data-testid="pk-admin-rev">
              <div className="label">Admin fee</div>
              <div className="font-display text-xl font-bold mt-1">£{pnl.revenue.admin_gbp.toLocaleString()}</div>
              <div className="text-xs muted">on BTM energy</div>
            </div>
            <div className="p-4 border border-[var(--line)]" data-testid="pk-opex">
              <div className="label">OpEx (pro-rated)</div>
              <div className="font-display text-xl font-bold mt-1">£{pnl.opex_gbp.toLocaleString()}</div>
              <div className="text-xs muted">{pnl.rates.opex_source === "indurent_model" ? "from Investor Model" : "£/kWp × kWp"}</div>
            </div>
            <div className={`p-4 border ${pnl.net_gbp >= 0 ? "bg-[var(--accent)] text-white border-[var(--accent)]" : "bg-red-600 text-white border-red-600"}`} data-testid="pk-net">
              <div className="label" style={{ color: "rgba(255,255,255,0.78)" }}>Net P&amp;L</div>
              <div className="font-display text-2xl font-bold mt-1">£{pnl.net_gbp.toLocaleString()}</div>
              <div className="text-xs" style={{ color: "rgba(255,255,255,0.85)" }}>
                Total rev £{pnl.revenue.total_gbp.toLocaleString()}
                {pnl.effective_rate_p_per_kwh != null && ` · ${pnl.effective_rate_p_per_kwh}p effective`}
              </div>
            </div>
          </div>

          {pnl.series.length > 0 ? (
            <div style={{ width: "100%", minWidth: 0, height: 320 }} className="swiss-card-flat p-4" data-testid="pnl-chart">
              <ResponsiveContainer width="99%" height="100%" minWidth={1} minHeight={1}>
                <ComposedChart data={pnl.series} margin={{ top: 10, right: 10, bottom: 4, left: 0 }}>
                  <CartesianGrid stroke="#E4E4E7" vertical={false} />
                  <XAxis dataKey="ts" tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA" />
                  <YAxis tick={{ fontSize: 10, fontFamily: "IBM Plex Mono" }} stroke="#A1A1AA"
                         tickFormatter={(v) => `£${v}`} />
                  <Tooltip contentStyle={{ borderRadius: 0, fontSize: 11 }}
                           formatter={(v, n) => [`£${Number(v).toFixed(2)}`, n]} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="ppa_gbp" name="PPA revenue" stackId="rev" fill="#00D6DF" />
                  <Bar dataKey="export_gbp" name="Export revenue" stackId="rev" fill="#314041" />
                  <Bar dataKey="admin_gbp" name="Admin fee" stackId="rev" fill="#B33C86" />
                  <Bar dataKey="opex_gbp" name="OpEx" stackId="cost" fill="#EF4444" />
                  <Line type="monotone" dataKey="net_gbp" name="Net P&L" stroke="#0A0A0A" strokeWidth={2.5} dot={false} />
                  <ReferenceLine y={0} stroke="#A1A1AA" strokeDasharray="2 2" />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="swiss-card-flat p-6 text-center text-xs muted" data-testid="pnl-empty">
              No P&amp;L data yet — sync production first (rates + OpEx are ready from the Investor Model).
            </div>
          )}
        </section>
      )}

      {data.series.length > 0 && (
        <section className="swiss-card-flat overflow-hidden" data-testid="site-samples-table">
          <div className="p-6 pb-3 label">
            {data.range === "24h" ? "15-minute samples" : "Daily rollup"}
          </div>
          <div className="max-h-[480px] overflow-y-auto">
            <table className="data-table">
              <thead className="sticky top-0 bg-[var(--surface)]">
                <tr>
                  <th>Timestamp</th>
                  <th className="num">Actual (kWh)</th>
                  <th className="num">Estimate (kWh)</th>
                  <th className="num">Variance</th>
                  <th className="num">GHI (W/m²)</th>
                </tr>
              </thead>
              <tbody>
                {[...data.series].reverse().slice(0, 400).map(r => (
                  <tr key={r.ts} data-testid={`sample-row-${r.ts}`}>
                    <td className="font-mono text-xs">{r.ts}</td>
                    <td className="num font-mono">{r.actual_kwh?.toLocaleString() ?? "—"}</td>
                    <td className="num font-mono muted">{r.estimated_kwh?.toLocaleString() ?? "—"}</td>
                    <td className={`num font-mono ${r.variance_pct != null && r.variance_pct < -10 ? "text-red-600" : r.variance_pct != null && r.variance_pct >= 0 ? "text-emerald-700" : ""}`}>
                      {r.variance_pct != null ? `${r.variance_pct > 0 ? "+" : ""}${r.variance_pct.toFixed(1)}%` : "—"}
                    </td>
                    <td className="num font-mono muted">{r.ghi_wh_per_m2 ?? "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* Full Monitoring tab embed — brings grouping toggles, series
          toggles, forecast overlay, CSV/PDF export, multi-site compare,
          weather backfill, monthly summary and PPA contracts into the
          Reporting → Analytics → [site] view without duplicating code. */}
      {project && (
        <section className="mt-10 pt-8 border-t border-[var(--line)]"
                 data-testid="site-monitoring-embed">
          <div className="mb-5">
            <div className="label">Deep-dive · monitoring workbench</div>
            <div className="font-display text-lg font-semibold mt-1">
              Interactive chart · exports · multi-site compare
            </div>
            <p className="text-xs muted mt-1">
              Same controls available under Projects → {project.name} → Monitoring — surfaced here for convenience.
            </p>
          </div>
          <MonitoringTab
            projectId={projectId}
            project={project}
            design={design}
            monitoring={monitoring}
            reload={load}
          />
        </section>
      )}

      {showMeterImport && (
        <MeterDataImportDialog
          projectId={projectId}
          onClose={() => setShowMeterImport(false)}
          onImported={() => load()}
        />
      )}
    </div>
  );
}
